import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toggleModal } from 'store/ui/actions'

import { setMode } from 'store/login-modal/actions'
import styles from './styles/WhatIsThisReportModal.css'
import Header from 'components/typography/Header'
import P from '../typography/Paragraph'
import * as analytics from 'lib/analytics'
import Modal from 'components/misc/Modal'
import Link from 'components/misc/Link'
import HorizontalTally from 'components/misc/HorizontalTally'

const getTruncatedAuthor = ({ authors }) => {
  if (!authors || authors.length === 0) {
    return ''
  }
  authors = [...authors].sort(
    (a, b) => a?.authorSequenceNumber - b?.authorSequenceNumber
  )
  return `${authors[0].family} et al.`
}

const WhatIsThisReportModal = ({ paper }) => {
  const dispatch = useDispatch()
  const whatIsThisReportModalShow = useSelector(({ ui }) => ui.whatIsThisReportModalShow)
  const user = useSelector(({ ui }) => ui.user)
  const fetchingUser = useSelector(({ ui }) => ui.fetchingUser)
  useEffect(() => {
    if (!whatIsThisReportModalShow) return
    analytics.event({
      category: 'Onboarding',
      action: 'View Report Help Modal'
    })
  }, [whatIsThisReportModalShow])

  const handleClose = () => {
    dispatch(toggleModal('whatIsThisReport', false))
  }

  const handleSignUp = () => {
    dispatch(toggleModal('whatIsThisReport', false))
    dispatch(setMode('register'))
    dispatch(toggleModal('login'))
  }

  const truncatedAuthor = getTruncatedAuthor({
    authors: paper?.authors
  })

  return (
    <Modal
      toggle={handleClose}
      show={whatIsThisReportModalShow}
      className={styles.modal}
    >
      <div className={styles.layout}>
        <div className={styles.headerContainer}>
          <Header className={styles.header} level={4}>Help me understand this report</Header>
        </div>
        <hr />

        <Header className={styles.header} level={6}>What is this page?</Header>
        <P>
          This is a scite report page which shows you exactly how <span className={styles.bold}>{paper?.title}</span> by <span className={styles.bold}>{truncatedAuthor}</span> was cited in publications that came afterwards.
        </P>
        <P>
          Specifically, you can see which publications used <span className={styles.italic}>{truncatedAuthor}</span> as a reference. Moreover, from each of these publications, you can see:
          <ul>
            <li>
              all the instances they referenced <span className={styles.italic}>{truncatedAuthor}</span> in-text, aka "Citation Statements"
            </li>
            <li>
              the section each Citation Statement occurred in
            </li>
            <li>
              a classification from our system indicating whether the Citation Statement offers evidence to support or contrast the findings from <span className={styles.italic}>{truncatedAuthor}</span> or mentions them in any way.
            </li>
          </ul>
        </P>

        <Header className={styles.header} level={6}>What are citation statements?</Header>
        <P>
          A citation statement is the textual context from a paper where it uses a reference. Our system extracts these sentences and maps the network of citations so you can see which papers cite each other and read exactly what they say about each other’s findings.
        </P>

        <Header className={styles.header} level={6}>What do supporting, mentioning, contrasting mean?</Header>
        <P>
          Our system classifies extracted Citation Statements as mentioning the referenced work, or indicating supporting or contrasting evidence for its findings. This is different from a simple positive and negative sentiment as it requires there to be some indication of the new work having similar or different results from the prior claims.
        </P>

        <Header className={styles.header} level={6}>What is the badge underneath the titles?</Header>
        <P>
          The badge, reproduced below, shows you a few different metrics about a particular paper.
        </P>
        <HorizontalTally tally={{ citingPublications: 200, supporting: 20, contradicting: 22, mentioning: 2 }} />
        <P>
          In order from left to right, you can see how many:
          <ul>
            <li>
              papers it was cited by
            </li>
            <li>
              supporting statements we have indexed from those papers
            </li>
            <li>
              mentioning statements we have indexed from those papers
            </li>
            <li>
              contrasting statements we have indexed from those papers
            </li>
            <li>
              retractions or withdrawals it has received, if any
            </li>
            <li>
              editorial notices such as corrections, errata, expressions of concern it has received, if any
            </li>
          </ul>
        </P>
        <P>
          By clicking on the badge, you can load that paper’s scite report to read citation statements about how its claims were received, allowing you to chain citations forward and backward in time.
        </P>

        <Header className={styles.header} level={6}>Where can I learn more about how scite works?</Header>
        <P>
          Information about our system is published and available for you to <Link blue underline target='_blank' rel='noopener noreferrer' href='https://direct.mit.edu/qss/article/2/3/882/102990/scite-A-smart-citation-index-that-displays-the'>read here.</Link> You can also {(!user && !fetchingUser) && (<span><Link blue underline onClick={handleSignUp}>sign up for an account</Link> and poke around, </span>)}<Link blue underline to='/blog'>browse our help</Link>, or <Link blue underline target='_blank' rel='noopener noreferrer' href='https://www.youtube.com/watch?v=RAPq3FnXp7k&list=PLHn2nrhhPBltNk12i0dIBGGRGSM-TpG5a'>watch videos we published</Link> to orient you.
        </P>
      </div>
    </Modal>
  )
}

export default WhatIsThisReportModal
